import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Sparkles, Heart } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  recommended: { label: "Recommended", color: "emerald", Icon: CheckCircle2 },
  caution: { label: "Caution", color: "amber", Icon: AlertTriangle },
  avoid: { label: "Avoid", color: "red", Icon: XCircle },
  neutral: { label: "Neutral", color: "slate", Icon: MinusCircle },
};

export default function FoodDetail() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [explanLoading, setExplanLoading] = useState(false);
  const [fav, setFav] = useState(false);
  const [favBusy, setFavBusy] = useState(false);

  useEffect(() => {
    api.get(`/foods/${id}/evaluation`).then((r) => setEv(r.data));
    api.get("/favorites").then((r) => {
      setFav(Array.isArray(r.data) && r.data.some((f) => f.food_id === id));
    }).catch(() => {});
  }, [id]);

  const toggleFav = async () => {
    setFavBusy(true);
    try {
      if (fav) {
        await api.delete(`/favorites/${id}`);
        setFav(false);
        toast.success("Removed from favorites");
      } else {
        await api.post("/favorites", { food_id: id });
        setFav(true);
        toast.success("Added to favorites");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update favorite");
    } finally { setFavBusy(false); }
  };

  const askAi = async () => {
    setExplanLoading(true);
    try {
      const r = await api.post(`/foods/${id}/explain`);
      setExplanation(r.data.explanation || "");
    } finally {
      setExplanLoading(false);
    }
  };

  if (!ev) {
    return <div className="p-10 text-slate-500">Loading…</div>;
  }

  const meta = STATUS_META[ev.status] || STATUS_META.neutral;
  const Icon = meta.Icon;
  const f = ev.food;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <Link to="/foods" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="back-to-foods">
        <ArrowLeft className="w-4 h-4" /> Back to foods
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-3xl border-emerald-100 p-6 fade-up">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-xs text-emerald-700 font-semibold tracking-wider uppercase">{f.category}</div>
                <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">{f.name}</h1>
              </div>
              <Badge className={`bg-${meta.color}-50 text-${meta.color}-700 border-${meta.color}-100 rounded-full px-3 py-1.5 text-sm`} data-testid="food-status-badge">
                <Icon className="w-4 h-4 mr-1.5" /> {meta.label}
              </Badge>
            </div>

            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                onClick={toggleFav}
                disabled={favBusy}
                className={`rounded-full ${fav ? "bg-red-50 border-red-200 text-red-700 hover:bg-red-100" : "border-emerald-200 hover:bg-emerald-50"}`}
                data-testid="favorite-toggle-btn"
              >
                <Heart className={`w-4 h-4 mr-2 ${fav ? "fill-red-500 text-red-500" : ""}`} />
                {fav ? "Favorited" : "Add to favorites"}
              </Button>
            </div>

            {f.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {f.tags.map((t) => (
                  <Badge key={t} variant="outline" className="rounded-full text-xs border-sky-200 text-sky-700 bg-sky-50">{t}</Badge>
                ))}
              </div>
            )}
          </Card>

          {/* Reasons */}
          <Card className="rounded-3xl border-emerald-100 p-6 fade-up delay-1">
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Why this rating, for you</h2>
            {ev.reasons.length === 0 ? (
              <p className="text-slate-500 text-sm">No specific concerns or strong benefits based on your profile.</p>
            ) : (
              <ul className="space-y-2.5" data-testid="reason-list">
                {ev.reasons.map((r, i) => {
                  const positive = !/(high|spike|raise|limit|avoid|trigger|risk|worsen|aggravat)/i.test(r) || /(potassium|fiber|omega|antioxidant|protein|calcium)/i.test(r);
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full mt-2 ${positive ? "bg-emerald-500" : "bg-amber-500"}`} />
                      <span className="text-sm text-slate-700 leading-relaxed">{r}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* AI explanation */}
          <Card className="rounded-3xl border-emerald-100 p-6 fade-up delay-2 bg-gradient-to-br from-white to-emerald-50/40">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <h2 className="font-display text-xl font-semibold text-slate-900">Deeper explanation</h2>
              </div>
              {!explanation && (
                <Button onClick={askAi} disabled={explanLoading} variant="outline" className="rounded-full border-emerald-200 hover:bg-emerald-50" data-testid="ai-explain-btn">
                  {explanLoading ? "Thinking…" : "Ask AI"}
                </Button>
              )}
            </div>
            {explanation ? (
              <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line" data-testid="ai-explanation">{explanation}</p>
            ) : (
              <p className="text-slate-400 text-sm">Get a personalized explanation in plain language.</p>
            )}
          </Card>
        </div>

        {/* Nutrition card */}
        <Card className="rounded-3xl border-emerald-100 p-6 h-fit fade-up delay-1">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Nutrition <span className="text-xs font-normal text-slate-400">per 100g</span></h3>
          <Row label="Calories" value={`${f.kcal} kcal`} />
          <Row label="Carbohydrates" value={`${f.carbs_g} g`} />
          <Row label="Sugar" value={`${f.sugar_g} g`} />
          <Row label="Fiber" value={`${f.fiber_g} g`} />
          <Row label="Protein" value={`${f.protein_g} g`} />
          <Row label="Total fat" value={`${f.fat_g} g`} />
          <Row label="Saturated fat" value={`${f.sat_fat} g`} />
          <Row label="Sodium" value={`${f.sodium_mg} mg`} />
          <Row label="Potassium" value={`${f.potassium_mg} mg`} />
          <Row label="Calcium" value={`${f.calcium_mg} mg`} />
          <Row label="Glycemic index" value={`${f.gi || "—"}`} />
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm border-b border-emerald-50 last:border-0">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}
